import React, { useState, useMemo } from "react";
import api from "@/api";
import { AREA_OPTIONS } from "@/lib/areaOptions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { OperatorMultiSelect } from "@/components/ui/operator-multi-select";
import { isRoleCompatibleWithManagementType } from "@/lib/notificationRouting";
import { toast } from "sonner";
import { usePagination } from "@/lib/usePagination";
import {
  Search,
  PlusCircle,
  Clock,
  CheckCircle2,
  AlertTriangle,
  CalendarClock,
  X,
  Eye,
  EyeOff,
  UserCircle2,
  Calendar,
  Trash2,
  Pencil,
  Building2,
  Briefcase,
  CircleDot,
  Copy
} from "lucide-react";
import { formatDate, getDaysUntil } from "@/utils/helpers";
import { cn } from "@/lib/utils";

/**
 * Individual Deadline Row Component
 * CRITICAL: This component must maintain STABLE DOM structure to avoid React 19 reconciliation errors.
 * ALL conditional rendering uses CSS visibility (hidden class), NOT JSX conditionals.
 */
function DeadlineRow({ 
  deadline, 
  caseItem, 
  client, 
  urgency, 
  daysUntil, 
  isCompleted, 
  operatorName, 
  notes, 
  toggleDeadlineStatus 
}) {
  const urgencyBorder = {
    "Scaduta": "border-l-red-500",
    "Critica": "border-l-red-400",
    "Urgente": "border-l-orange-500",
    "Importante": "border-l-yellow-500",
    "Normale": "border-l-blue-400",
    "Completata": "border-l-green-500"
  };

  // Pre-compute all conditional values
  const showDaysUntil = !isCompleted && daysUntil !== null;
  const showOpeningDate = Boolean(caseItem?.openingDate);
  const showNotes = Boolean(notes);
  const showOperator = Boolean(operatorName);

  // Compute days text
  let daysText = "";
  if (showDaysUntil) {
    if (daysUntil < 0) {
      daysText = `Scaduta da ${Math.abs(daysUntil)}g`;
    } else if (daysUntil === 0) {
      daysText = "Scade oggi!";
    } else {
      daysText = `${daysUntil}g rimanenti`;
    }
  }

  return (
    <div 
      className={cn(
        "bg-white border border-slate-100 rounded-lg border-l-4 p-3 hover:shadow-sm transition-shadow",
        urgencyBorder[urgency] || "border-l-slate-300",
        isCompleted && "opacity-60"
      )}
    >
      <div className="flex items-start gap-3">
        {/* 1. CLIENT - Primary info column - ALWAYS RENDERED */}
        <div className="w-40 shrink-0">
          <p className={cn(
            "font-semibold text-slate-900 truncate text-sm",
            isCompleted && "line-through text-slate-500"
          )}>
            {client?.fullName || "—"}
          </p>
          <p className="text-xs text-slate-400 truncate">
            {caseItem?.caseNumber || "—"}
          </p>
        </div>

        {/* 2. TITOLO / PRATICA - Main content - ALWAYS RENDERED */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={cn(
              "text-sm font-medium truncate",
              isCompleted && "line-through text-slate-400"
            )}>
              {deadline.title}
            </h4>
            <Badge variant="outline" className={cn(
              "shrink-0 text-xs px-1.5 py-0",
              urgency === "Scaduta" && "border-red-300 text-red-700 bg-red-50",
              urgency === "Critica" && "border-red-300 text-red-700 bg-red-50",
              urgency === "Urgente" && "border-orange-300 text-orange-700 bg-orange-50",
              urgency === "Completata" && "border-green-300 text-green-700 bg-green-50",
              (urgency === "Normale" || urgency === "Importante") && "border-slate-200 text-slate-600"
            )}>
              {urgency}
            </Badge>
          </div>
          {/* 5. NOTE - ALWAYS RENDERED, hidden via CSS when empty */}
          <p className={cn(
            "text-xs text-slate-400 truncate mt-0.5 italic",
            !showNotes && "hidden"
          )}>
            {notes || ""}
          </p>
        </div>

        {/* 4. OPERATORE - ALWAYS RENDERED, hidden via CSS when not assigned */}
        <div className={cn("w-28 shrink-0 text-center", !showOperator && "hidden")}>
          <div className="flex items-center justify-center gap-1 text-xs text-indigo-600 bg-indigo-50 rounded px-2 py-1">
            <UserCircle2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{operatorName || ""}</span>
          </div>
        </div>

        {/* 3. DATA / SCADENZA - ALWAYS RENDERED with stable structure */}
        <div className="text-right shrink-0 w-32">
          <p className="text-sm text-slate-700 font-medium">{formatDate(deadline.dueDate)}</p>
          {/* Days until - ALWAYS RENDERED, hidden via CSS */}
          <p className={cn(
            "text-xs",
            !showDaysUntil && "hidden",
            daysUntil < 0 ? "text-red-600 font-medium" : 
            daysUntil <= 3 ? "text-orange-600" : "text-slate-400"
          )}>
            {daysText}
          </p>
          {/* Data avvio pratica - ALWAYS RENDERED, hidden via CSS */}
          <p className={cn(
            "text-xs text-slate-400 mt-0.5 flex items-center justify-end gap-1",
            !showOpeningDate && "hidden"
          )}>
            <Calendar className="h-3 w-3" />
            <span>Avvio: {showOpeningDate ? formatDate(caseItem.openingDate) : ""}</span>
          </p>
        </div>

        {/* Action Button - ALWAYS RENDERED */}
        <Button
          size="sm"
          variant={isCompleted ? "ghost" : "outline"}
          className={cn(
            "shrink-0 self-center h-8 px-2",
            !isCompleted && "text-green-600 hover:text-green-700 hover:bg-green-50"
          )}
          onClick={() => toggleDeadlineStatus(deadline.id)}
        >
          <CheckCircle2 className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline text-xs">
            {isCompleted ? "Riapri" : "Completa"}
          </span>
        </Button>
      </div>
    </div>
  );
}

/**
 * Scadenze Tab Component
 * Shows deadline statistics, filtering, and CRUD operations
 * 
 * CRITICAL: To avoid React 19 DOM reconciliation errors:
 * 1. ALL conditional rendering uses CSS classes (hidden), NOT JSX ternaries
 * 2. DeadlineRow is a separate component with stable DOM structure
 * 3. Empty states are always rendered but hidden via CSS
 */
function ScadenzeTab({
  deadlines,
  cases,
  clients,
  operators,
  activeCases,
  searchQuery,
  setSearchQuery,
  filterClient,
  setFilterClient,
  isDeadlineDialogOpen,
  setIsDeadlineDialogOpen,
  deadlineForm,
  setDeadlineForm,
  editingDeadline,
  resetDeadlineForm,
  handleSaveDeadline,
  toggleDeadlineStatus,
  handleDeleteDeadline,
  getDeadlineUrgency,
  getOperatorName,
  operatorRole = "amministratore",
  defaultSubTab = null,
  onSubTabConsumed,
  onOpenNewClient,
  isClientDialogOpen: parentClientDialogOpen,
  clientForm: parentClientForm,
  setClientForm: parentSetClientForm,
  handleSaveClient: parentHandleSaveClient,
  setIsClientDialogOpen: parentSetIsClientDialogOpen,
  resetClientForm: parentResetClientForm,
  // 27/02/2026 — Diario → Scadenza: detail one-shot dal padre
  pendingDiaryDeadline = null,
  onPendingDiaryDeadlineConsumed = () => {},
}) {
  // Card filter state: "all" | "overdue" | "urgent" | "completed"
  const [cardFilter, setCardFilter] = useState("all");
  
  // Show/hide completed toggle (default: hide completed)
  const [showCompleted, setShowCompleted] = useState(false);

  // Management filter based on operator role
  const defaultMgmt = operatorRole === "operatore_studio" ? "studio" 
    : operatorRole === "operatore_agenzia" ? "agenzia" 
    : "all";
  const [managementFilter, setManagementFilter] = useState(defaultMgmt);

  // Sub-tab: "pratiche" or "extra"
  const [activeSubTab, setActiveSubTab] = useState("extra");
  
  // Date filter for Scadenze Pratiche
  const [dateFilterPratiche, setDateFilterPratiche] = useState("all");

  // Custom deadlines state
  const [customDeadlines, setCustomDeadlines] = useState([]);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [showExtraDialog, setShowExtraDialog] = useState(false);
  // Small confirmation dialog shown when marking a deadline as
  // "completata" so the operator can back-date or simply confirm today.
  // Carries the deadline being completed and the user-editable date.
  const [completionDialog, setCompletionDialog] = useState({
    open: false, item: null, completedAt: "",
  });
  const [extraForm, setExtraForm] = useState({
    clientName: "", clientId: "", practiceType: defaultMgmt === "all" ? "agenzia" : defaultMgmt,
    deadlineType: "", deadlineDate: "", notes: "", status: "attiva",
    // Phase 1 reminder + area fields. Defaults match the To-Do spec:
    // reminderStartDate auto-copies dueDate; reminderType="once";
    // areaType is only required when practiceType="studio".
    reminderStartDate: "", reminderType: "once", areaType: null,
    // Multi-assign (20/02/2026): co-recipients of the reminder.
    operatorId: "", operatorIds: [],
  });
  const [editingExtraId, setEditingExtraId] = useState(null);

  // Extra deadlines filters
  const [extraSearch, setExtraSearch] = useState("");
  const [extraDateFilter, setExtraDateFilter] = useState("all");
  const [extraDateFrom, setExtraDateFrom] = useState("");
  const [extraDateTo, setExtraDateTo] = useState("");
  const [extraYearFilter, setExtraYearFilter] = useState("all");
  const [extraMgmtFilter, setExtraMgmtFilter] = useState(defaultMgmt);
  const [showExtraCompleted, setShowExtraCompleted] = useState(false);

  // 27/02/2026 — Source linkage Diario → Scadenza. Quando il Diario
  // emette l'evento `gi:open-extra-deadline-from-diary`, qui salviamo
  // l'id della nota originaria e lo iniettiamo nel payload (campo
  // sourceId) al momento del salvataggio, così il backend appende
  // l'audit "deadline_created" alla history della nota.
  const sourceContactIdRef = React.useRef("");

  // Ref per il primo input del dialog "Nuova Scadenza Clienti".
  // Usato per forzare il focus quando il dialog viene aperto via
  // flow Diario (requestAnimationFrame defer impedisce l'auto-focus
  // standard di Radix → senza focus, anche ESC non funziona).
  const extraFirstInputRef = React.useRef(null);

  // Load custom deadlines
  const loadCustomDeadlines = React.useCallback(async () => {
    setIsLoadingCustom(true);
    try { setCustomDeadlines(await api.getCustomDeadlines()); }
    catch (e) { console.error("Load custom deadlines error:", e); }
    finally { setIsLoadingCustom(false); }
  }, []);

  React.useEffect(() => { loadCustomDeadlines(); }, [loadCustomDeadlines]);

  // 27/02/2026 — Diario → Nuova Scadenza Clienti. App.js mette
  // `pendingDiaryDeadline` come prop quando l'utente clicca "Crea
  // Scadenza" su una nota Diario. Fix v2 (bug blocking UI):
  //  • Usiamo requestAnimationFrame per spostare l'apertura del
  //    Radix Dialog al frame SUCCESSIVO al mount del componente, così
  //    qualsiasi residuo di overlay/portal della modale Diario
  //    (InlineModal con position:fixed z-index:50) è già stato
  //    smontato prima che Radix applichi `pointer-events:none` al body.
  //  • Consumiamo poi il flag con un piccolo delay aggiuntivo per
  //    evitare un secondo trigger della useEffect a causa della
  //    nuova reference di `onPendingDiaryDeadlineConsumed` (App.js
  //    crea una arrow per render).
  React.useEffect(() => {
    if (!pendingDiaryDeadline) return;
    const detail = pendingDiaryDeadline;
    sourceContactIdRef.current = detail.sourceContactId || "";
    const today = new Date().toISOString().split("T")[0];
    setEditingExtraId(null);
    setExtraForm({
      clientName: detail.clientName || "",
      clientId: detail.clientId || "",
      practiceType: detail.practiceType === "studio" ? "studio" : "agenzia",
      deadlineType: detail.deadlineType || "Follow-up",
      deadlineDate: today,
      notes: detail.notes || "",
      status: "attiva",
      reminderStartDate: today,
      reminderType: "once",
      areaType: null,
      operatorId: detail.assignedOperatorId || "",
      operatorIds: detail.assignedOperatorId ? [detail.assignedOperatorId] : [],
    });
    try { setActiveSubTab("extra"); } catch (_) {}
    const raf = requestAnimationFrame(() => {
      setShowExtraDialog(true);
      // Fix ESC bug: dopo il mount del Dialog Radix, forziamo il focus
      // al primo input. Senza questo, il flow via requestAnimationFrame
      // non riesce a stabilire correttamente il focus-trap e l'ESC
      // viene ignorato (Annulla button funziona perché è un click).
      setTimeout(() => {
        try { extraFirstInputRef.current?.focus(); } catch (_) {}
      }, 50);
      onPendingDiaryDeadlineConsumed();
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingDiaryDeadline, onPendingDiaryDeadlineConsumed]);

  // Auto-switch to sub-tab when navigated from dashboard
  React.useEffect(() => {
    if (defaultSubTab) {
      setActiveSubTab(defaultSubTab);
      if (onSubTabConsumed) onSubTabConsumed();
    }
  }, [defaultSubTab, onSubTabConsumed]);

  // Build case management map
  const caseManagementMap = useMemo(() => {
    const map = {};
    cases.forEach(c => { map[c.id] = c.practiceType; });
    return map;
  }, [cases]);

  // Calculate stats (active only for totale)
  const activeDeadlines = useMemo(() => 
    deadlines.filter(d => d.status !== "completata"), 
    [deadlines]
  );
  
  const overdueCount = useMemo(() => 
    deadlines.filter(d => {
      if (d.status === "completata") return false;
      return getDeadlineUrgency(d.dueDate, d.status) === "Scaduta";
    }).length,
    [deadlines, getDeadlineUrgency]
  );

  const urgentCount = useMemo(() => 
    deadlines.filter(d => {
      if (d.status === "completata") return false;
      const urgency = getDeadlineUrgency(d.dueDate, d.status);
      return urgency === "Critica" || urgency === "Urgente";
    }).length,
    [deadlines, getDeadlineUrgency]
  );

  const completedCount = useMemo(() => 
    deadlines.filter(d => d.status === "completata").length,
    [deadlines]
  );

  // Filter deadlines based on card filter AND search/client filters AND showCompleted toggle AND management
  const filteredDeadlines = useMemo(() => {
    return deadlines.filter(d => {
      // Management filter
      if (managementFilter !== "all") {
        const caseType = caseManagementMap[d.caseId];
        const targetType = managementFilter === "agenzia" ? "Gestionale Agenzia" : "Gestionale Studio";
        if (caseType && caseType !== targetType) return false;
      }
      
      // Date filter for Pratiche
      if (dateFilterPratiche !== "all" && d.dueDate) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dueDate = new Date(d.dueDate);
        if (dateFilterPratiche === "7giorni") {
          const from = new Date(today); from.setDate(from.getDate() - 7);
          if (dueDate < from) return false;
        } else if (dateFilterPratiche === "mese") {
          if (dueDate.getMonth() !== now.getMonth() || dueDate.getFullYear() !== now.getFullYear()) return false;
        } else if (dateFilterPratiche === "3mesi") {
          const from = new Date(today); from.setMonth(from.getMonth() - 3);
          if (dueDate < from) return false;
        }
      }

      // Hide completed by default (unless showCompleted is true or cardFilter is "completed")
      if (d.status === "completata" && !showCompleted && cardFilter !== "completed") {
        return false;
      }

      // Card filter
      if (cardFilter === "overdue") {
        if (d.status === "completata") return false;
        if (getDeadlineUrgency(d.dueDate, d.status) !== "Scaduta") return false;
      } else if (cardFilter === "urgent") {
        if (d.status === "completata") return false;
        const urgency = getDeadlineUrgency(d.dueDate, d.status);
        if (urgency !== "Critica" && urgency !== "Urgente") return false;
      } else if (cardFilter === "completed") {
        if (d.status !== "completata") return false;
      }
      // cardFilter === "all" shows active (and completed if showCompleted)

      // Search filter
      if (searchQuery) {
        const caseItem = cases.find(c => c.id === d.caseId);
        const client = clients.find(c => c.id === caseItem?.clientId);
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          d.title?.toLowerCase().includes(searchLower) ||
          caseItem?.caseNumber?.toLowerCase().includes(searchLower) ||
          caseItem?.subject?.toLowerCase().includes(searchLower) ||
          client?.fullName?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Client filter
      if (filterClient && filterClient !== "all") {
        const caseItem = cases.find(c => c.id === d.caseId);
        if (String(caseItem?.clientId) !== filterClient) return false;
      }
      
      return true;
    });
  }, [deadlines, cardFilter, showCompleted, searchQuery, filterClient, cases, clients, getDeadlineUrgency, managementFilter, caseManagementMap, dateFilterPratiche]);

  // Handle card click
  const handleCardClick = (filter) => {
    setCardFilter(cardFilter === filter ? "all" : filter);
  };

  // Get filter label for display
  const filterLabel = useMemo(() => {
    switch (cardFilter) {
      case "overdue": return "Scadute";
      case "urgent": return "In Scadenza";
      case "completed": return "Completate";
      default: return "";
    }
  }, [cardFilter]);

  // Pre-compute visibility flags
  const showFilterBadge = cardFilter !== "all";
  const hasDeadlines = filteredDeadlines.length > 0;

  // Paginazione condivisa (20/02/2026) — Scadenze fiscali.
  const {
    paged: pagedDeadlines,
    paginationBar: scadenzePaginationBar,
  } = usePagination(filteredDeadlines, {
    storageKey: "scadenze-fiscali",
    itemLabelSingular: "scadenza",
    itemLabelPlural: "scadenze",
  });

  // Filtered custom deadlines (always excludes completed - shown separately).
  // 27/02/2026 — Performance pass: pre-computiamo i Date una volta sola
  // fuori dal loop (era ricalcolato per ogni riga in `new Date(...)`),
  // applichiamo i filtri dal più selettivo (search testo) al più costoso.
  const filteredExtraDeadlines = useMemo(() => {
    const isStudio = extraMgmtFilter === "studio";
    const isAgenzia = extraMgmtFilter === "agenzia";
    const yearPrefix = extraYearFilter !== "all" ? extraYearFilter : null;
    const q = extraSearch.trim().toLowerCase();
    // Pre-calcolo range date una volta sola
    let fromIso = null, toIso = null;
    if (extraDateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const isoOf = (d) => d.toISOString().slice(0, 10);
      if (extraDateFilter === "7giorni") { const f = new Date(today); f.setDate(f.getDate() - 7); fromIso = isoOf(f); }
      else if (extraDateFilter === "15giorni") { const f = new Date(today); f.setDate(f.getDate() - 15); fromIso = isoOf(f); }
      else if (extraDateFilter === "mese") {
        const m = now.getMonth(), y = now.getFullYear();
        fromIso = `${y}-${String(m + 1).padStart(2, "0")}-01`;
        toIso = `${y}-${String(m + 1).padStart(2, "0")}-${new Date(y, m + 1, 0).getDate()}`;
      } else if (extraDateFilter === "meseprecedente") {
        const pm = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const py = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        fromIso = `${py}-${String(pm + 1).padStart(2, "0")}-01`;
        toIso = `${py}-${String(pm + 1).padStart(2, "0")}-${new Date(py, pm + 1, 0).getDate()}`;
      } else if (extraDateFilter === "3mesi") { const f = new Date(today); f.setMonth(f.getMonth() - 3); fromIso = isoOf(f); }
      else if (extraDateFilter === "range") { fromIso = extraDateFrom || null; toIso = extraDateTo || null; }
    }
    const list = [];
    for (const d of customDeadlines) {
      if (d.status === "completata") continue;
      if (isStudio && d.practiceType !== "studio") continue;
      if (isAgenzia && d.practiceType !== "agenzia") continue;
      if (yearPrefix && !d.deadlineDate?.startsWith(yearPrefix)) continue;
      if (fromIso && (!d.deadlineDate || d.deadlineDate < fromIso)) continue;
      if (toIso && (!d.deadlineDate || d.deadlineDate > toIso)) continue;
      if (q) {
        const cn = d.clientName?.toLowerCase() || "";
        const dt = d.deadlineType?.toLowerCase() || "";
        const nt = d.notes?.toLowerCase() || "";
        if (!cn.includes(q) && !dt.includes(q) && !nt.includes(q)) continue;
      }
      list.push(d);
    }
    return list.sort((a, b) => (a.deadlineDate || "").localeCompare(b.deadlineDate || ""));
  }, [customDeadlines, extraMgmtFilter, extraYearFilter, extraDateFilter, extraDateFrom, extraDateTo, extraSearch]);

  // 27/02/2026 — activeExtraDeadlines è ora una semplice reference: il
  // filtro per "completata" è già applicato in filteredExtraDeadlines.
  // Evitiamo il doppio filter precedente (2682 elementi ricontrollati).
  const activeExtraDeadlines = filteredExtraDeadlines;

  const completedExtraDeadlines = useMemo(() => {
    if (!showExtraCompleted) return [];
    let list = [...customDeadlines].filter(d => d.status === "completata");
    if (extraMgmtFilter !== "all") list = list.filter(d => d.practiceType === extraMgmtFilter);
    if (extraSearch.trim()) {
      const q = extraSearch.toLowerCase();
      list = list.filter(d => d.clientName?.toLowerCase().includes(q) || d.deadlineType?.toLowerCase().includes(q) || d.notes?.toLowerCase().includes(q));
    }
    return list.sort((a, b) => (b.deadlineDate || "").localeCompare(a.deadlineDate || ""));
  }, [customDeadlines, showExtraCompleted, extraMgmtFilter, extraSearch]);

  const extraYears = useMemo(() => {
    const years = new Set();
    customDeadlines.forEach(d => { if (d.deadlineDate) years.add(d.deadlineDate.slice(0, 4)); });
    return Array.from(years).sort().reverse();
  }, [customDeadlines]);

  // 27/02/2026 — Performance pass v1.
  // Su dataset reali la Scadenze Clienti supera già le 2.500 righe.
  // Renderizzare tutte le `<Card>` provoca freeze percepito (~1s)
  // ogni volta che il dataset cambia (save, complete, delete). Soluzione:
  // pre-calcoliamo i counter (memo), e mostriamo solo una pagina alla
  // volta dell'active list. La pagination viene resettata quando
  // cambia un filtro.
  const EXTRA_PAGE_SIZE = 50;
  const [extraPage, setExtraPage] = useState(1);
  // Reset paginazione quando cambia un filtro o cambiano i dati
  React.useEffect(() => { setExtraPage(1); }, [extraMgmtFilter, extraYearFilter, extraDateFilter, extraDateFrom, extraDateTo, extraSearch]);
  const completedExtraCount = useMemo(
    () => customDeadlines.reduce((n, d) => d.status === "completata" ? n + 1 : n, 0),
    [customDeadlines]
  );
  const activeExtraCount = useMemo(
    () => customDeadlines.length - completedExtraCount,
    [customDeadlines.length, completedExtraCount]
  );
  const pagedActiveDeadlines = useMemo(() => {
    return activeExtraDeadlines.slice(0, extraPage * EXTRA_PAGE_SIZE);
  }, [activeExtraDeadlines, extraPage]);

  const getExtraUrgency = (dateStr, status) => {
    if (status === "completata") return { color: "bg-green-100 text-green-700 border-green-300", dot: "bg-green-500", label: "Completata" };
    const days = getDaysUntil(dateStr);
    if (days < 0) return { color: "bg-red-100 text-red-700 border-red-300", dot: "bg-red-500", label: `Scaduta (${Math.abs(days)}gg)` };
    if (days <= 7) return { color: "bg-red-50 text-red-600 border-red-200", dot: "bg-red-400", label: `${days}gg` };
    if (days <= 30) return { color: "bg-yellow-50 text-yellow-700 border-yellow-300", dot: "bg-yellow-500", label: `${days}gg` };
    return { color: "bg-green-50 text-green-700 border-green-300", dot: "bg-green-500", label: `${days}gg` };
  };

  const handleSaveExtra = async () => {
    // Phase 1 client-side validation — mirror server-side rules so the
    // operator gets immediate feedback. The backend still validates as
    // the source of truth.
    const dueDate = (extraForm.deadlineDate || "").trim();
    let reminderStart = (extraForm.reminderStartDate || "").trim();
    if (!reminderStart) reminderStart = dueDate; // auto-copy
    if (reminderStart && dueDate && reminderStart > dueDate) {
      toast.error('"Ricordamelo dal" non può essere successivo a "Data Scadenza"');
      return;
    }
    if (extraForm.practiceType === "studio" && !extraForm.areaType) {
      toast.error('Per la Gestione Studio il campo "Area" è obbligatorio');
      return;
    }

    // Sentinel `__custom` (UI-only): risolto a `once` se l'utente ha già
    // scelto la data dal calendario, altrimenti chiediamo di completare.
    if (extraForm.reminderType === "__custom") {
      if (!extraForm.reminderStartDate) {
        toast.error('Seleziona una data dal calendario per il promemoria personalizzato');
        return;
      }
    }

    try {
      let formToSave = {
        ...extraForm,
        reminderStartDate: reminderStart,
        reminderType: extraForm.reminderType === "__custom" ? "once" : (extraForm.reminderType || "once"),
        // Force-strip areaType when switching to agenzia so the doc
        // stays consistent with the backend invariants.
        areaType: extraForm.practiceType === "studio" ? extraForm.areaType : null,
      };
      // If name typed but no client selected, try to match by exact name
      if (formToSave.clientName && !formToSave.clientId) {
        const existingMatch = clients.find(c => c.fullName?.toLowerCase() === formToSave.clientName.toLowerCase());
        if (existingMatch) {
          formToSave.clientId = existingMatch.id;
        }
      }
      // 27/02/2026 — Source linkage Diario → Scadenza: se la modale
      // è stata aperta da una nota Diario, propaghiamo sourceModule/
      // sourceId al payload così il backend appende l'audit alla nota.
      if (!editingExtraId && sourceContactIdRef.current) {
        formToSave.sourceModule = "diario";
        formToSave.sourceId = sourceContactIdRef.current;
      }
      if (editingExtraId) {
        const updated = await api.updateCustomDeadline(editingExtraId, formToSave);
        setCustomDeadlines(prev => prev.map(d => d.id === editingExtraId ? updated : d));
        toast.success("Scadenza aggiornata");
      } else {
        const created = await api.createCustomDeadline(formToSave);
        // 27/02/2026 — Inseriamo IN TESTA così l'operatore la vede
        // immediatamente dopo il save (altrimenti finisce in fondo
        // alla lista delle 2000+ scadenze e sembra non salvata).
        setCustomDeadlines(prev => [created, ...prev]);
        // Diary roundtrip: se la scadenza nasce dal Diario, notifica
        // al Diario di ricaricare la nota così l'audit entry
        // "deadline_created" è subito visibile in cronologia.
        if (formToSave.sourceModule === "diario" && formToSave.sourceId) {
          try { window.dispatchEvent(new CustomEvent("gi:diary-refresh")); } catch (_) {}
          toast.success("Scadenza creata e collegata alla nota Diario", {
            description: `${created.clientName || ""} — ${created.deadlineType || "Follow-up"}`,
          });
        } else {
          toast.success("Scadenza creata", {
            description: `${created.clientName || ""} — ${created.deadlineType || ""}`,
          });
        }
        // Reset sempre il source ref dopo create (anche in caso di
        // errore lo facciamo nel finally implicito del catch sotto).
        sourceContactIdRef.current = "";
      }
      setShowExtraDialog(false); setEditingExtraId(null);
      setExtraForm({
        clientName: "", clientId: "",
        practiceType: defaultMgmt === "all" ? "agenzia" : defaultMgmt,
        deadlineType: "", deadlineDate: "", notes: "", status: "attiva",
        reminderStartDate: "", reminderType: "once", areaType: null,
        operatorId: "", operatorIds: [],
      });
    } catch (e) {
      console.error("Save extra deadline error:", e);
      // Pulizia source ref anche in caso di errore per evitare
      // contaminazione a successivi save manuali (+ Nuova).
      sourceContactIdRef.current = "";
      toast.error(e?.message || "Errore nel salvataggio della scadenza");
    }
  };

  const handleDeleteExtra = async (id) => {
    if (!confirm("Eliminare questa scadenza?")) return;
    try { await api.deleteCustomDeadline(id); setCustomDeadlines(prev => prev.filter(d => d.id !== id)); }
    catch (e) { console.error(e); }
  };

  const handleToggleExtraStatus = async (item) => {
    // Reverting to "attiva" is direct, no dialog needed.
    if (item.status === "completata") {
      try {
        const u = await api.updateCustomDeadline(item.id, {
          ...item, status: "attiva", completedAt: null,
        });
        setCustomDeadlines(prev => prev.map(d => d.id === item.id ? u : d));
      } catch (e) { console.error(e); }
      return;
    }
    // Mark as completed → open the date confirmation dialog so the
    // operator can confirm or back-date the completion. We default the
    // editable date to today (ISO yyyy-mm-dd, browser-local).
    const today = new Date().toISOString().slice(0, 10);
    setCompletionDialog({ open: true, item, completedAt: today });
  };

  const handleConfirmCompletion = async () => {
    const { item, completedAt } = completionDialog;
    if (!item) return;
    try {
      const u = await api.updateCustomDeadline(item.id, {
        ...item,
        status: "completata",
        completedAt: completedAt || new Date().toISOString().slice(0, 10),
      });
      setCustomDeadlines(prev => prev.map(d => d.id === item.id ? u : d));
      setCompletionDialog({ open: false, item: null, completedAt: "" });
      toast.success("Scadenza completata. Promemoria correlati chiusi.");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Errore nel completamento");
    }
  };

  const handleDuplicateExtra = (item) => {
    setEditingExtraId(null);
    setExtraForm({
      clientName: item.clientName,
      clientId: item.clientId || "",
      practiceType: item.practiceType || "agenzia",
      deadlineType: item.deadlineType || "",
      deadlineDate: "",
      notes: item.notes || "",
      status: "attiva",
      reminderStartDate: "",
      reminderType: item.reminderType || "once",
      areaType: item.practiceType === "studio" ? (item.areaType || null) : null,
      operatorId: item.operatorId || "",
      operatorIds: item.operatorIds || (item.operatorId ? [item.operatorId] : []),
    });
    setShowExtraDialog(true);
  };

  // Client search state for new deadline dialog
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [pendingDeadlineForm, setPendingDeadlineForm] = useState(null);
  const clientSuggestions = useMemo(() => {
    if (!extraForm.clientName || extraForm.clientName.length < 2) return [];
    const q = extraForm.clientName.toLowerCase();
    return clients.filter(c => c.fullName?.toLowerCase().includes(q)).slice(0, 8);
  }, [extraForm.clientName, clients]);

  // When client dialog closes after creating from deadline flow, link the new client
  React.useEffect(() => {
    if (pendingDeadlineForm && !parentClientDialogOpen) {
      // Find the newly created client by name
      const newClient = clients.find(c => 
        c.fullName?.toLowerCase() === pendingDeadlineForm.clientName?.toLowerCase()
      );
      if (newClient) {
        setExtraForm(f => ({ ...f, clientName: newClient.fullName, clientId: newClient.id }));
      }
      setPendingDeadlineForm(null);
      setShowExtraDialog(true);
    }
  }, [parentClientDialogOpen, pendingDeadlineForm, clients]);

  const handleOpenNewClient = () => {
    // Save current deadline form state
    setPendingDeadlineForm({ ...extraForm });
    setShowExtraDialog(false);
    // Open client dialog with pre-filled name
    if (parentResetClientForm) parentResetClientForm();
    if (parentSetClientForm) parentSetClientForm(f => ({ ...f, fullName: extraForm.clientName }));
    if (onOpenNewClient) onOpenNewClient();
  };

  return (
    <div className="space-y-4">
      {/* Page Title + Management Filter */}
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-slate-600" />
          Scadenzario
        </h2>
      </div>

      {/* Sub-tabs: Clienti first (primary) */}
      <div className="flex gap-2 border-b border-slate-200 pb-1">
        <button onClick={() => setActiveSubTab("extra")} className={cn("px-4 py-2 text-sm font-medium rounded-t-lg transition-colors", activeSubTab === "extra" ? "bg-orange-600 text-white" : "text-slate-500 hover:bg-slate-100")} data-testid="subtab-extra">
          <CircleDot className="h-4 w-4 inline mr-1.5" />Scadenze Clienti ({activeExtraCount})
        </button>
        <button onClick={() => setActiveSubTab("pratiche")} className={cn("px-4 py-2 text-sm font-medium rounded-t-lg transition-colors", activeSubTab === "pratiche" ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100")} data-testid="subtab-pratiche">
          <CalendarClock className="h-4 w-4 inline mr-1.5" />Scadenze Pratiche
        </button>
      </div>

      {/* === SCADENZE EXTRA === */}
      <div className={cn(activeSubTab !== "extra" && "hidden")}>
        <Card className="shadow-sm mb-3"><CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cerca cliente, tipo, note..." value={extraSearch} onChange={e => setExtraSearch(e.target.value)} className="pl-8 h-8 text-sm" />
          </div>
          <Select value={extraMgmtFilter} onValueChange={setExtraMgmtFilter}>
            <SelectTrigger className={cn("w-[110px] h-8 text-xs", extraMgmtFilter === "studio" && "border-blue-400 bg-blue-50", extraMgmtFilter === "agenzia" && "border-orange-400 bg-orange-50")}><SelectValue /></SelectTrigger>
            <SelectContent>
              {(operatorRole === "amministratore" || operatorRole === "responsabile") && <SelectItem value="all">Tutte</SelectItem>}
              <SelectItem value="agenzia">Agenzia</SelectItem>
              <SelectItem value="studio">Studio</SelectItem>
            </SelectContent>
          </Select>
          <Select value={extraDateFilter} onValueChange={setExtraDateFilter}>
            <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le date</SelectItem>
              <SelectItem value="7giorni">Ultimi 7 giorni</SelectItem>
              <SelectItem value="15giorni">Ultimi 15 giorni</SelectItem>
              <SelectItem value="mese">Mese corrente</SelectItem>
              <SelectItem value="meseprecedente">Mese precedente</SelectItem>
              <SelectItem value="3mesi">Ultimi 3 mesi</SelectItem>
              <SelectItem value="range">Periodo personalizzato</SelectItem>
            </SelectContent>
          </Select>
          {extraDateFilter === "range" && <>
            <Input type="date" value={extraDateFrom} onChange={e => setExtraDateFrom(e.target.value)} className="w-[125px] h-8 text-xs" />
            <Input type="date" value={extraDateTo} onChange={e => setExtraDateTo(e.target.value)} className="w-[125px] h-8 text-xs" />
          </>}
          {extraYears.length > 0 && <Select value={extraYearFilter} onValueChange={setExtraYearFilter}>
            <SelectTrigger className="w-[90px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Anno</SelectItem>{extraYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>}
          <Button size="sm" className="h-8 bg-orange-600 hover:bg-orange-700" data-testid="new-extra-deadline-btn" onClick={() => {
            setEditingExtraId(null);
            const today = new Date().toISOString().slice(0, 10);
            setExtraForm({
              clientName: "", clientId: "",
              practiceType: extraMgmtFilter === "all" ? "agenzia" : extraMgmtFilter,
              deadlineType: "", deadlineDate: today, notes: "", status: "attiva",
              reminderStartDate: today, reminderType: "once", areaType: null,
              operatorId: "", operatorIds: [],
            });
            setShowExtraDialog(true);
          }}>
            <PlusCircle className="h-4 w-4 mr-1" /> Nuova
          </Button>
        </CardContent></Card>

        {isLoadingCustom ? <p className="text-center text-slate-400 py-8">Caricamento...</p>
        : <>
          {/* Active deadlines - paginated to avoid 2500+ Card render freeze */}
          {activeExtraDeadlines.length > 0 ? <div className="space-y-2">{pagedActiveDeadlines.map(item => {
          const urg = getExtraUrgency(item.deadlineDate, item.status);
          return (
            <Card key={item.id} className={cn("shadow-sm border-l-4 hover:shadow-md", urg.color)} data-testid={`extra-${item.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className={cn("h-3 w-3 rounded-full shrink-0", urg.dot)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{item.clientName}</span>
                    <Badge variant="outline" className={cn("text-[10px]", item.practiceType === "studio" ? "border-blue-300 text-blue-600" : "border-orange-300 text-orange-600")}>{item.practiceType === "studio" ? "Studio" : "Agenzia"}</Badge>
                    {item.practiceType === "studio" && item.areaType && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px]",
                          item.areaType === "Lavoro"
                            ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                            : "border-violet-300 text-violet-700 bg-violet-50"
                        )}
                        data-testid={`extra-area-badge-${item.id}`}
                      >
                        {item.areaType}
                      </Badge>
                    )}
                    {item.reminderType && item.reminderType !== "none" && item.reminderType !== "once" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-sky-300 text-sky-700 bg-sky-50"
                        data-testid={`extra-reminder-badge-${item.id}`}
                        title="Tipo promemoria"
                      >
                        {item.reminderType === "daily" ? "Ogni giorno"
                          : item.reminderType === "weekly" ? "Ogni settimana"
                          : item.reminderType === "monthly" ? "Ogni mese"
                          : item.reminderType === "yearly" ? "Ogni anno"
                          : item.reminderType}
                      </Badge>
                    )}
                    <span className="text-xs text-slate-500">{item.deadlineType}</span>
                  </div>
                  {item.notes && <p className="text-xs text-slate-500 mt-0.5 truncate">{item.notes}</p>}
                  {item.reminderStartDate && item.reminderStartDate !== item.deadlineDate && (
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Ricordamelo dal {formatDate(item.reminderStartDate)}
                    </p>
                  )}
                  {item.status === "completata" && item.completedAt && (
                    <p className="text-[11px] text-emerald-600 mt-0.5" data-testid={`extra-completed-at-${item.id}`}>
                      Completata il {formatDate(item.completedAt)}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium">{formatDate(item.deadlineDate)}</p>
                  <Badge className={cn("text-[10px] mt-0.5", urg.color)}>{urg.label}</Badge>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleToggleExtraStatus(item)} data-testid={`complete-extra-${item.id}`}><CheckCircle2 className={cn("h-4 w-4", item.status === "completata" ? "text-green-500" : "text-slate-300")} /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDuplicateExtra(item)} title="Duplica scadenza" data-testid={`duplicate-extra-${item.id}`}><Copy className="h-3.5 w-3.5 text-slate-400" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                    setEditingExtraId(item.id);
                    // Hydrate legacy records: fall back to safe defaults
                    // so the new "Ricordamelo dal" / "Tipo promemoria"
                    // inputs always carry a coherent value.
                    setExtraForm({
                      ...item,
                      reminderStartDate: item.reminderStartDate || item.deadlineDate || "",
                      reminderType: item.reminderType || "once",
                      areaType: item.practiceType === "studio" ? (item.areaType || null) : null,
                      operatorId: item.operatorId || "",
                      operatorIds: item.operatorIds || (item.operatorId ? [item.operatorId] : []),
                    });
                    setShowExtraDialog(true);
                  }}><Pencil className="h-3.5 w-3.5 text-slate-400" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteExtra(item.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}</div> : <Card><CardContent className="py-6 text-center text-slate-400 text-sm">Nessuna scadenza attiva trovata</CardContent></Card>}

          {/* Paginazione - mostra "+ Mostra altri" se ci sono ancora righe nascoste */}
          {activeExtraDeadlines.length > pagedActiveDeadlines.length && (
            <div className="flex items-center justify-center py-3" data-testid="extra-load-more-wrapper">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => setExtraPage(p => p + 1)}
                data-testid="extra-load-more-btn"
              >
                Mostra altri {Math.min(EXTRA_PAGE_SIZE, activeExtraDeadlines.length - pagedActiveDeadlines.length)} di {activeExtraDeadlines.length - pagedActiveDeadlines.length} rimanenti
              </Button>
            </div>
          )}

          {/* Toggle button — between active and completed */}
          {completedExtraCount > 0 && (
            <div className="flex items-center justify-center gap-2 py-3 my-3 border-y border-slate-200">
              <Switch id="show-extra-completed" checked={showExtraCompleted} onCheckedChange={setShowExtraCompleted} />
              <Label htmlFor="show-extra-completed" className="text-xs text-slate-500 cursor-pointer">
                {showExtraCompleted ? "Nascondi" : "Mostra"} scadenze completate ({completedExtraCount})
              </Label>
            </div>
          )}

          {/* Completed deadlines — below the toggle */}
          {showExtraCompleted && completedExtraDeadlines.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-4 w-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Scadenze Completate ({completedExtraDeadlines.length})</h3>
              </div>
              <div className="space-y-1.5">
                {completedExtraDeadlines.map(item => (
                  <Card key={item.id} className="shadow-none border border-slate-100 bg-slate-50/50 opacity-70" data-testid={`extra-completed-${item.id}`}>
                    <CardContent className="p-2.5 flex items-center gap-3">
                      <div className="h-2.5 w-2.5 rounded-full shrink-0 bg-slate-300" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-slate-400 line-through">{item.clientName}</span>
                          <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-400">{item.practiceType === "studio" ? "Studio" : "Agenzia"}</Badge>
                          <span className="text-xs text-slate-400">{item.deadlineType}</span>
                        </div>
                        {item.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{item.notes}</p>}
                        {item.completedAt && (
                          <p className="text-[11px] text-emerald-600 mt-0.5" data-testid={`extra-completed-at-${item.id}`}>
                            Completata il {formatDate(item.completedAt)}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-slate-400">{formatDate(item.deadlineDate)}</p>
                        <Badge className="text-[10px] mt-0.5 bg-green-50 text-green-600 border-green-200">Completata</Badge>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleToggleExtraStatus(item)} title="Riapri scadenza"><CheckCircle2 className="h-4 w-4 text-green-500" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDuplicateExtra(item)} title="Duplica scadenza"><Copy className="h-3.5 w-3.5 text-slate-300" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-300 hover:text-red-500" onClick={() => handleDeleteExtra(item.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>}

        <Dialog open={showExtraDialog} onOpenChange={setShowExtraDialog}>
          <DialogContent
            className="max-w-md"
            onPointerDownOutside={(e) => {
              // Allow closing unless the click is on the autocomplete dropdown
              if (clientSearchOpen) e.preventDefault();
            }}
            onEscapeKeyDown={() => {
              // 27/02/2026 — Fix esplicito ESC per il flow Diario:
              // quando il dialog viene aperto via requestAnimationFrame
              // il focus-trap Radix non si stabilisce subito e ESC viene
              // ignorato. Garantiamo manualmente la chiusura.
              setShowExtraDialog(false);
            }}
          >
            <DialogHeader><DialogTitle>{editingExtraId ? "Modifica Scadenza" : "Nuova Scadenza Clienti"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome Cliente *</Label>
                <div className="relative">
                  <Input 
                    ref={extraFirstInputRef}
                    value={extraForm.clientName} 
                    onChange={e => {
                      const val = e.target.value;
                      setExtraForm(f => ({ ...f, clientName: val, clientId: "" }));
                      setClientSearchOpen(val.length >= 2);
                    }}
                    onFocus={() => { if (extraForm.clientName.length >= 2) setClientSearchOpen(true); }}
                    onBlur={() => setTimeout(() => setClientSearchOpen(false), 200)}
                    placeholder="Cerca o inserisci cliente..."
                    autoComplete="off"
                  />
                  {clientSearchOpen && clientSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {clientSuggestions.map(c => (
                        <button key={c.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setExtraForm(f => ({ ...f, clientName: c.fullName, clientId: c.id }));
                            setClientSearchOpen(false);
                          }}>
                          <UserCircle2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span>{c.fullName}</span>
                          {c.fiscalCode && <span className="text-xs text-slate-400 ml-auto">{c.fiscalCode}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {clientSearchOpen && extraForm.clientName.length >= 2 && clientSuggestions.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-3">
                      <p className="text-xs text-slate-500 mb-2">Nessun cliente trovato per "{extraForm.clientName}"</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setClientSearchOpen(false);
                          handleOpenNewClient();
                        }}
                        data-testid="create-client-from-deadline"
                      >
                        <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                        Nuovo Cliente
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Seleziona dall'anagrafica o digita un nuovo nome</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Gestione</Label><Select value={extraForm.practiceType} onValueChange={v => setExtraForm(f => ({
                  ...f,
                  practiceType: v,
                  // Switching to Agenzia clears the Area field (kept only
                  // for Gestione Studio).
                  areaType: v === "studio" ? f.areaType : null,
                }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="agenzia">Agenzia</SelectItem><SelectItem value="studio">Studio</SelectItem></SelectContent></Select></div>
                <div><Label>Data Scadenza *</Label><Input type="date" value={extraForm.deadlineDate} onChange={e => {
                  const v = e.target.value;
                  setExtraForm(f => ({
                    ...f,
                    deadlineDate: v,
                    // Auto-bump the reminder start when it slips past
                    // the new due date, or when it's still empty.
                    reminderStartDate:
                      !f.reminderStartDate || (v && f.reminderStartDate > v)
                        ? v
                        : f.reminderStartDate,
                  }));
                }} data-testid="extra-due-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ricordamelo dal</Label>
                  <Input
                    type="date"
                    value={extraForm.reminderStartDate || ""}
                    max={extraForm.deadlineDate || undefined}
                    onChange={e => setExtraForm(f => ({ ...f, reminderStartDate: e.target.value }))}
                    placeholder={extraForm.deadlineDate || ""}
                    data-testid="extra-reminder-start"
                  />
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Se vuoto coincide con la Data Scadenza
                  </p>
                </div>
                <div>
                  <Label>Tipo promemoria</Label>
                  <Select
                    value={extraForm.reminderType === "__custom" ? "__custom" : (extraForm.reminderType || "once")}
                    onValueChange={v => {
                      if (v === "__custom") {
                        // "Personalizza..." è UI-only: apre un calendario
                        // inline. Sul backend salviamo `reminderType="once"`
                        // + `reminderStartDate=<data scelta>` (vedi onSelect
                        // più sotto). Finché l'utente non sceglie un giorno,
                        // teniamo lo stato visivo `__custom` per mostrare il
                        // calendario aperto.
                        setExtraForm(f => ({ ...f, reminderType: "__custom" }));
                      } else {
                        setExtraForm(f => ({ ...f, reminderType: v }));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="extra-reminder-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      <SelectItem value="once">Una sola volta</SelectItem>
                      <SelectItem value="daily">Ogni giorno</SelectItem>
                      <SelectItem value="weekly">Ogni settimana</SelectItem>
                      <SelectItem value="monthly">Ogni mese</SelectItem>
                      <SelectItem value="yearly">Ogni anno</SelectItem>
                      <SelectItem value="__custom" data-testid="extra-reminder-type-custom">Personalizza…</SelectItem>
                    </SelectContent>
                  </Select>
                  {extraForm.reminderType === "__custom" && (
                    <div className="mt-2 rounded-md border border-slate-200 bg-white p-2 shadow-sm" data-testid="extra-reminder-custom-calendar">
                      <p className="text-[11px] text-slate-500 mb-1.5 px-1">
                        Scegli il giorno in cui far comparire il promemoria
                      </p>
                      <CalendarPicker
                        mode="single"
                        locale={itLocale}
                        selected={extraForm.reminderStartDate ? new Date(extraForm.reminderStartDate + "T00:00:00") : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          // Persistiamo come ISO YYYY-MM-DD + `reminderType=once`
                          // (singola occorrenza nel giorno scelto). Lo sentinel
                          // UI `__custom` viene risolto a `once` qui.
                          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          setExtraForm(f => ({ ...f, reminderType: "once", reminderStartDate: iso }));
                        }}
                        disabled={(d) => {
                          // Non consenti di promemoriare nel passato. Il
                          // confronto è solo sulla data (h 00:00).
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return d < today;
                        }}
                        initialFocus
                        data-testid="extra-reminder-custom-picker"
                      />
                    </div>
                  )}
                </div>
              </div>
              {extraForm.practiceType === "studio" && (
                <div data-testid="extra-area-row">
                  <Label>Area *</Label>
                  <Select
                    value={extraForm.areaType || ""}
                    onValueChange={v => setExtraForm(f => ({ ...f, areaType: v }))}
                  >
                    <SelectTrigger data-testid="extra-area-select"><SelectValue placeholder="Seleziona area..." /></SelectTrigger>
                    <SelectContent>
                      {AREA_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Obbligatorio per le scadenze di Gestione Studio
                  </p>
                </div>
              )}
              <div><Label>Tipo Pratica *</Label><Input value={extraForm.deadlineType} onChange={e => setExtraForm(f => ({ ...f, deadlineType: e.target.value }))} placeholder="Es. Rinnovo licenza, DURC..." /></div>
              <div><Label>Dettaglio</Label><Textarea value={extraForm.notes || ""} onChange={e => setExtraForm(f => ({ ...f, notes: e.target.value }))} placeholder="Dettagli sulla scadenza..." rows={2} /></div>
              {/* Operatore Assegnato (multi-select, 20/02/2026): la
                  notifica scadenza viene inviata a tutti gli operatori
                  selezionati. Lista filtrata per management compatibility. */}
              <div>
                <Label>Operatore Assegnato</Label>
                <OperatorMultiSelect
                  operators={(operators || []).filter(op => {
                    if (!op || op.status === "sospeso") return false;
                    const mt = extraForm.practiceType === "studio" ? "studio" : "agenzia";
                    return isRoleCompatibleWithManagementType(op.role, mt);
                  })}
                  value={extraForm.operatorIds && extraForm.operatorIds.length
                    ? extraForm.operatorIds
                    : (extraForm.operatorId ? [extraForm.operatorId] : [])}
                  onChange={(list) => {
                    setExtraForm(f => ({
                      ...f,
                      operatorId: list[0] || "",
                      operatorIds: list,
                    }));
                  }}
                  placeholder="Nessuno — promemoria solo ad admin/responsabile"
                  dataTestid="extra-deadline-operators"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Se vuoto, il promemoria va solo ad amministratore/responsabile.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtraDialog(false)}>Annulla</Button>
              <Button onClick={handleSaveExtra} disabled={
                !extraForm.clientName ||
                !extraForm.deadlineDate ||
                !extraForm.deadlineType ||
                (extraForm.practiceType === "studio" && !extraForm.areaType)
              }>{editingExtraId ? "Salva" : "Crea Scadenza"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* === SCADENZE PRATICHE === */}
      <div className={cn(activeSubTab !== "pratiche" && "hidden")}>

      {/* Compact summary pills + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button onClick={() => handleCardClick("overdue")} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all", cardFilter === "overdue" ? "bg-red-600 text-white border-red-600" : "bg-white border-red-200 text-red-700 hover:bg-red-50")} data-testid="filter-card-overdue">
          <span className="font-bold text-sm">{overdueCount}</span> Scadute
        </button>
        <button onClick={() => handleCardClick("urgent")} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all", cardFilter === "urgent" ? "bg-orange-500 text-white border-orange-500" : "bg-white border-orange-200 text-orange-700 hover:bg-orange-50")} data-testid="filter-card-urgent">
          <span className="font-bold text-sm">{urgentCount}</span> In Scadenza
        </button>
        <button onClick={() => handleCardClick("completed")} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all", cardFilter === "completed" ? "bg-green-600 text-white border-green-600" : "bg-white border-green-200 text-green-700 hover:bg-green-50")} data-testid="filter-card-completed">
          <span className="font-bold text-sm">{completedCount}</span> Completate
        </button>
        <button onClick={() => setCardFilter("all")} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all", cardFilter === "all" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-blue-200 text-blue-700 hover:bg-blue-50")} data-testid="filter-card-all">
          <span className="font-bold text-sm">{activeDeadlines.length}</span> Attive
        </button>
        {cardFilter && cardFilter !== "all" && (
          <button onClick={() => setCardFilter("all")} className="text-xs text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>

      {/* Filters row: Management + Search + Date + Completed toggle */}
      <Card className="shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={managementFilter} onValueChange={setManagementFilter}>
              <SelectTrigger className={cn("w-[110px] h-8 text-xs font-medium",
                managementFilter === "studio" && "border-blue-400 bg-blue-50 text-blue-700",
                managementFilter === "agenzia" && "border-orange-400 bg-orange-50 text-orange-700"
              )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(operatorRole === "amministratore" || operatorRole === "responsabile") && <SelectItem value="all">Tutte</SelectItem>}
                <SelectItem value="agenzia">Agenzia</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1 relative min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Cerca pratica, cliente..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 h-8 text-sm" data-testid="search-deadlines-input" />
            </div>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-deadline-client-select"><SelectValue placeholder="Cliente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {clients.map((client) => <SelectItem key={client.id} value={String(client.id)}>{client.fullName}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dateFilterPratiche} onValueChange={setDateFilterPratiche}>
              <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le date</SelectItem>
                <SelectItem value="7giorni">Ultimi 7 giorni</SelectItem>
                <SelectItem value="15giorni">Ultimi 15 giorni</SelectItem>
                <SelectItem value="mese">Mese corrente</SelectItem>
                <SelectItem value="meseprecedente">Mese precedente</SelectItem>
                <SelectItem value="3mesi">Ultimi 3 mesi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {/* Dialog + Add button moved here */}
      <Dialog open={isDeadlineDialogOpen} onOpenChange={setIsDeadlineDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    resetDeadlineForm();
                    setIsDeadlineDialogOpen(true);
                  }}
                  data-testid="new-deadline-button"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Nuova Scadenza
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingDeadline ? "Modifica Scadenza" : "Nuova Scadenza"}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="deadlineTitle">Titolo Scadenza *</Label>
                    <Input
                      id="deadlineTitle"
                      data-testid="deadline-title-input"
                      value={deadlineForm.title}
                      onChange={(e) =>
                        setDeadlineForm({ ...deadlineForm, title: e.target.value })
                      }
                      placeholder="Es. Invio UNILAV, Comunicazione DTL..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="deadlineCaseId">Pratica Collegata *</Label>
                    <Select
                      value={deadlineForm.caseId}
                      onValueChange={(value) =>
                        setDeadlineForm({ ...deadlineForm, caseId: value })
                      }
                    >
                      <SelectTrigger data-testid="deadline-case-select">
                        <SelectValue placeholder="Seleziona pratica" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeCases.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.caseNumber} - {c.subject}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="deadlineDueDate">Data Scadenza *</Label>
                    <Input
                      id="deadlineDueDate"
                      type="date"
                      data-testid="deadline-due-date-input"
                      value={deadlineForm.dueDate}
                      onChange={(e) =>
                        setDeadlineForm({ ...deadlineForm, dueDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="deadlineDescription">Descrizione</Label>
                    <Textarea
                      id="deadlineDescription"
                      data-testid="deadline-description-textarea"
                      value={deadlineForm.description}
                      onChange={(e) =>
                        setDeadlineForm({ ...deadlineForm, description: e.target.value })
                      }
                      rows={3}
                      placeholder="Descrizione dettagliata della scadenza..."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={resetDeadlineForm}>
                    Annulla
                  </Button>
                  <Button data-testid="save-deadline-button" onClick={handleSaveDeadline}>
                    {editingDeadline ? "Aggiorna" : "Crea Scadenza"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

      {/* Lista Scadenze */}
      <div className="space-y-3">
        {/* Empty State - ALWAYS RENDERED, hidden via CSS when has items */}
        <Card className={cn("shadow-md", hasDeadlines && "hidden")}>
          <CardContent className="text-center py-12">
            <CalendarClock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessuna scadenza trovata</p>
            <p className="text-sm text-slate-400 mt-1">
              {(searchQuery || filterClient !== "all") 
                ? "Prova a modificare i filtri"
                : "Le scadenze create appariranno qui"}
            </p>
          </CardContent>
        </Card>
        
        {/* Deadlines List - Each row is a separate stable component */}
        {pagedDeadlines.map((deadline) => {
          const caseItem = cases.find(c => c.id === deadline.caseId);
          const client = clients.find(c => c.id === caseItem?.clientId);
          const urgency = getDeadlineUrgency(deadline.dueDate, deadline.status);
          const daysUntil = getDaysUntil(deadline.dueDate);
          const isCompleted = deadline.status === "completata";
          const operatorName = caseItem?.assignedOperatorId && getOperatorName 
            ? getOperatorName(caseItem.assignedOperatorId) 
            : "";
          const notes = deadline.description || deadline.notes || "";

          return (
            <DeadlineRow
              key={deadline.id}
              deadline={deadline}
              caseItem={caseItem}
              client={client}
              urgency={urgency}
              daysUntil={daysUntil}
              isCompleted={isCompleted}
              operatorName={operatorName}
              notes={notes}
              toggleDeadlineStatus={toggleDeadlineStatus}
            />
          );
        })}

        {scadenzePaginationBar}

      {/* Bottom toggle: show completed pratiche deadlines */}
      {deadlines.some(d => d.status === "completata") && (
        <div className="flex items-center justify-center gap-2 pt-4 border-t border-slate-100 mt-4">
          <Switch id="show-completed" checked={showCompleted} onCheckedChange={setShowCompleted} data-testid="toggle-show-completed" />
          <Label htmlFor="show-completed" className="text-xs text-slate-500 cursor-pointer">
            Mostra scadenze completate ({deadlines.filter(d => d.status === "completata").length})
          </Label>
        </div>
      )}
      </div>
      </div>

      {/*
        Completion confirmation dialog. Opens when the operator clicks
        the "complete" toggle on an active deadline (see
        `handleToggleExtraStatus`). The form is intentionally tiny —
        a date picker prefilled with today plus Conferma / Annulla —
        because the cascade (stop reminders + close related popup
        notifications) is handled server-side; the operator only needs
        to confirm or back-date.
      */}
      <Dialog
        open={completionDialog.open}
        onOpenChange={(o) => !o && setCompletionDialog({ open: false, item: null, completedAt: "" })}
      >
        <DialogContent
          className="max-w-sm"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="deadline-completion-dialog"
        >
          <DialogHeader>
            <DialogTitle>Conferma completamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {completionDialog.item && (
              <div className="text-sm text-slate-700">
                <div className="font-semibold">{completionDialog.item.clientName}</div>
                <div className="text-xs text-slate-500">{completionDialog.item.deadlineType}</div>
                {completionDialog.item.deadlineDate && (
                  <div className="text-xs text-slate-400 mt-1">
                    Scadenza: {formatDate(completionDialog.item.deadlineDate)}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="completion-date">Data completamento</Label>
              <Input
                id="completion-date"
                type="date"
                value={completionDialog.completedAt}
                onChange={(e) => setCompletionDialog(s => ({ ...s, completedAt: e.target.value }))}
                data-testid="completion-date-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Predefinita: oggi. Puoi modificarla se la scadenza è stata chiusa in passato.
              </p>
            </div>
            <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 leading-relaxed">
              Confermando il completamento i promemoria attivi verranno
              fermati e le notifiche correlate spostate in &quot;Completate&quot;.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCompletionDialog({ open: false, item: null, completedAt: "" })}
              data-testid="completion-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleConfirmCompletion}
              disabled={!completionDialog.completedAt}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="completion-confirm-btn"
            >
              Conferma completamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default React.memo(ScadenzeTab);
